import React, { useState, useCallback, useMemo } from 'react'
import { Icon } from 'util/ui'
import SpacedItems from 'shared/components/ui/SpacedItems'
import { BoxHeadline } from 'shared/components/ui/Typography'

import themeVars from 'ui_theme/site/globals/site.variables'

import styles from './styles.less'

const ColumnHeaders = React.memo(
  ({ onChange, sorting, headers, headerPrefix }) => {
    const { key, direction } = sorting

    const onClick = useCallback(
      () => {
        onChange(key)
      },
      [onChange, key]
    )

    // TODO (jscheel): The concept of a headerPrefix absolutely sucks. If you
    // have any ideas on a better approach, please refactor :)

    return (
      <div className={styles.ColumnHeaders}>
        <SpacedItems.Container verticalAlign="center">
          {headerPrefix && (
            <SpacedItems.Item key="prefix">{headerPrefix}</SpacedItems.Item>
          )}
          {headers.map(
            ({ key: itemKey, sortKey, title, sortable = true, flexBasis }) =>
              sortable ? (
                <SpacedItems.Item key={itemKey} basis={flexBasis}>
                  <HeaderCell
                    sortKey={sortKey}
                    title={title}
                    currentSortKey={key}
                    currentSortDirection={direction}
                    onClick={onClick}
                  />
                </SpacedItems.Item>
              ) : (
                <HeaderCellNoSort title={title} />
              )
          )}
        </SpacedItems.Container>
      </div>
    )
  }
)

const directionIcons = {
  asc: 'up',
  desc: 'down',
}
const HeaderCell = ({
  onClick,
  sortKey,
  title,
  currentSortDirection,
  currentSortKey,
}) => {
  const active = sortKey === currentSortKey
  let iconName = 'sort'
  if (active) {
    iconName = `caret ${directionIcons[currentSortDirection]}`
  }

  return (
    <BoxHeadline
      size="small"
      className={styles.HeaderCell}
      // eslint-disable-next-line react/jsx-no-bind
      onClick={() => onClick(sortKey)}
    >
      {title}{' '}
      <Icon
        name={iconName}
        color={active ? themeVars.blackColorName : undefined}
      />
    </BoxHeadline>
  )
}

const HeaderCellNoSort = ({ title }) => {
  return (
    <BoxHeadline size="small" className={styles.HeaderCellNoSort}>
      {title}{' '}
    </BoxHeadline>
  )
}

const SortableList = ({
  children,
  headers,
  headerPrefix,
  listPrefix,
  initialSortKey = 'id',
  initialSortDirection = 'asc',
}) => {
  const [sorting, setSorting] = useState({
    key: initialSortKey,
    direction: initialSortDirection,
  })

  const handleColumnChange = useCallback(
    newKey => {
      setSorting(currentSorting => {
        if (newKey === currentSorting.key) {
          return {
            key: newKey,
            direction: currentSorting.direction === 'asc' ? 'desc' : 'asc',
          }
        }
        return {
          key: newKey,
          direction: 'asc',
        }
      })
    },
    [setSorting]
  )

  const sortedChildren = useMemo(
    () => {
      const collator = new Intl.Collator('en', {
        numeric: true,
        sensitivity: 'base',
      })
      const result = React.Children.toArray(children)
      const { direction, key } = sorting
      if (direction === 'asc') {
        result.sort((a, b) => collator.compare(a.props[key], b.props[key]))
      } else {
        result.sort((a, b) => collator.compare(b.props[key], a.props[key]))
      }
      return result
    },
    [children, sorting]
  )

  return (
    <React.Fragment>
      <ColumnHeaders
        onChange={handleColumnChange}
        sorting={sorting}
        headers={headers}
        headerPrefix={headerPrefix}
      />
      {listPrefix}
      <React.Fragment>{sortedChildren}</React.Fragment>
    </React.Fragment>
  )
}

export default SortableList
